// @ts-nocheck
/**
 * GPU track layer for the columnar live-map path (flag-gated; mounted only when config.LIVE_MAP_GPU).
 *
 * Replaces the D3-SVG `LiveTrackLayer` for live replay: one Mapbox GL `line` layer + GeoJSON source per
 * source, fed from `liveColumnarStore.trackCoords()` (typed-array slice → [lng,lat][]). Mapbox owns pan/zoom
 * on the GPU, so there is NO per-render `map.project` reprojection on the main thread. `setData` runs on the
 * track cadence (throttled), not per frame.
 *
 * Isolation: live-map only. Reads the columnar store + playback clock; touches no shared map component.
 */

import { createEffect, onCleanup } from 'solid-js';
import { liveColumnarStore } from '../../../../../store/liveColumnarStore';
import { sourcesStore } from '../../../../../store/sourcesStore';
import { liveConfigStore } from '../../../../../store/liveConfigStore';
import { resolveTrackColor } from '../../renderers/boatMarkerStyle';
import {
  smoothPlaybackTimeForBoat,
  isPlaying,
  isLiveReplayPlayheadReady,
} from '../../../../../store/playbackStore';
import type { LiveReplaySourceStyle } from '../../../../../utils/liveReplayFramePoints';
import {
  hasHighlightedSources,
  highlightedSources,
  isSourceHighlighted,
} from '../../../../../store/liveTrackHighlightStore';
import { debug } from '../../../../../utils/console';

const LIVE_MAP_DEFAULT_TIME_WINDOW_MIN = 5;
/** GeoJSON setData cadence — tracks are slow context; the boat layer handles smooth 5 Hz motion. */
const TRACK_SETDATA_THROTTLE_MS = 1000;
/**
 * setData cadence while PAUSED/scrubbing.
 *
 * 1 Hz is right for a following map: the track is slow context behind a boat that is already moving
 * smoothly, and at 20 Hz of inbound data a faster rebuild is pure waste. It is wrong for a scrub,
 * where the track IS the thing being moved — at 1 Hz the boats follow the drag while the trail
 * visibly lags a second behind them, which reads as breakage rather than as latency.
 *
 * A scrub is bounded user input, not a continuous feed, so the rebuild cost (a window slice plus one
 * Mapbox GeoJSON parse, a few thousand points) is affordable for the length of a drag.
 */
const TRACK_SCRUB_SETDATA_THROTTLE_MS = 150;

/** Line widths and opacities for the three emphasis states a track can be in. */
const TRACK_WIDTH_NORMAL = 1;
const TRACK_WIDTH_HIGHLIGHTED = 3;
const TRACK_OPACITY_NORMAL = 1;
const TRACK_OPACITY_DIMMED = 0.2;

export interface GlTrackLayerProps {
  map: any;
  selectedSourceIds: Set<number>;
  sourceStyles?: Map<number, LiveReplaySourceStyle>;
  replayTimeWindowMin?: number;
}

const srcId = (sourceId: number) => `live-gl-track-src-${sourceId}`;
const lyrId = (sourceId: number) => `live-gl-track-lyr-${sourceId}`;

export default function GlTrackLayer(props: GlTrackLayerProps) {
  const added = new Set<number>();
  let lastSetDataAt = 0;
  /** Pending trailing redraw, so the LAST scrub position always lands (see the throttle below). */
  let trailingTimer: ReturnType<typeof setTimeout> | null = null;
  /** Per-source count of the coords last pushed, to tell "no data yet" from "genuinely empty". */
  const lastCoordCount = new Map<number, number>();

  const styleReady = () => {
    const map = props.map;
    return !!map && typeof map.isStyleLoaded === 'function' && map.isStyleLoaded();
  };

  // Color strictly by the boat's NAME resolved from the stream source id (arrow source_registry). Do NOT index
  // sourceStyles by the numeric id here: layers are keyed by the server's stream id, which can differ from the
  // frontend selection id sourceStyles is keyed by — indexing by id would color a track with another boat's color.
  const colorFor = (frameId: number): string => {
    const name = liveColumnarStore.sourceName(frameId);
    return resolveTrackColor(name, (name && sourcesStore.getSourceColor(name)) || '#1f77b4');
  };

  // Resolve a selected source to the server's stream-assigned source id by NAME (via the arrow source_registry).
  // Layers/tracks are keyed by this stream id — NOT the raw selection id — so a class boat that is not being
  // streamed this session (no registry entry) is skipped instead of colliding with another boat's track.
  const frameIdFor = (selectedId: number): number | undefined => {
    const name = props.sourceStyles?.get(selectedId)?.sourceName;
    return name ? liveColumnarStore.sourceId(name) : undefined;
  };

  const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
    let h = String(hex).replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6) return null;
    const n = parseInt(h, 16);
    if (!Number.isFinite(n)) return null;
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  };

  const emptyFc = () => ({ type: 'FeatureCollection', features: [] });
  const lineFeature = (coords: number[][]) => ({
    type: 'FeatureCollection',
    features:
      coords.length >= 2
        ? [{ type: 'Feature', geometry: { type: 'LineString', coordinates: coords }, properties: {} }]
        : [],
  });

  const ensureLayer = (map: any, sourceId: number) => {
    if (added.has(sourceId)) return;
    if (map.getSource(srcId(sourceId))) {
      added.add(sourceId);
      return;
    }
    try {
      // lineMetrics enables line-progress -> fade the trail from faint (oldest, progress 0) to full (newest).
      map.addSource(srcId(sourceId), { type: 'geojson', data: emptyFc(), lineMetrics: true });
      const color = colorFor(sourceId);
      const rgb = hexToRgb(color);
      const paint: Record<string, unknown> = { 'line-width': 1 };
      if (rgb) {
        paint['line-gradient'] = [
          'interpolate', ['linear'], ['line-progress'],
          0, `rgba(${rgb.r},${rgb.g},${rgb.b},0.05)`,
          0.6, `rgba(${rgb.r},${rgb.g},${rgb.b},0.5)`,
          1, `rgba(${rgb.r},${rgb.g},${rgb.b},1)`,
        ];
      } else {
        paint['line-color'] = color;
        paint['line-opacity'] = 0.9;
      }
      map.addLayer({
        id: lyrId(sourceId),
        type: 'line',
        source: srcId(sourceId),
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint,
      });
      added.add(sourceId);
      // A layer added while an emphasis is already active must not appear at full weight for a
      // frame before the effect catches up.
      applyEmphasis();
    } catch (err) {
      debug('[GlTrackLayer] addSource/addLayer failed', err);
    }
  };

  const removeLayer = (map: any, sourceId: number) => {
    try {
      if (map.getLayer(lyrId(sourceId))) map.removeLayer(lyrId(sourceId));
      if (map.getSource(srcId(sourceId))) map.removeSource(srcId(sourceId));
    } catch (_) {
      /* map may be torn down */
    }
    added.delete(sourceId);
  };

  /** Selected source ids mapped to the server's streamed source ids (by name); non-streamed sources dropped. */
  const streamedFrameIds = (): Set<number> => {
    const selected = props.selectedSourceIds || new Set<number>();
    const out = new Set<number>();
    for (const id of selected) {
      const fid = frameIdFor(id);
      if (fid !== undefined) out.add(fid);
    }
    return out;
  };

  // Add/prune layers as the selection changes.
  createEffect(() => {
    const map = props.map;
    void liveColumnarStore.epoch(); // re-run when the source_registry / data arrives so names resolve
    if (!styleReady()) return;
    const frameIds = streamedFrameIds();
    for (const fid of frameIds) ensureLayer(map, fid);
    for (const fid of [...added]) if (!frameIds.has(fid)) removeLayer(map, fid);
  });

  /**
   * Push current geometry for every streamed source.
   *
   * Extracted from the effect below so the trailing-edge timer can re-run it. Reads made from that
   * timer are outside a tracking scope, which is what we want: it must draw the CURRENT playhead,
   * not re-subscribe to anything. The effect keeps the subscriptions.
   */
  const redraw = () => {
    const map = props.map;
    const selected = props.selectedSourceIds || new Set<number>();
    const playhead = smoothPlaybackTimeForBoat();
    if (!styleReady() || selected.size === 0) return;

    const playheadMs = playhead instanceof Date ? playhead.getTime() : 0;
    if (!isLiveReplayPlayheadReady(playheadMs)) return;

    // Paused means the user is driving the playhead by hand; follow the input, not the feed cadence.
    const throttleMs = isPlaying() ? TRACK_SETDATA_THROTTLE_MS : TRACK_SCRUB_SETDATA_THROTTLE_MS;
    const now = Date.now();
    if (now - lastSetDataAt < throttleMs) {
      // TRAILING EDGE. This used to just `return`, dropping the update entirely. While following
      // that self-heals — the next rAF epoch redraws a moment later — but a scrub is a burst that
      // ENDS, so the final playhead position was the one most likely to be thrown away and the
      // track stayed a second behind where the user let go. Schedule the tail of the burst.
      if (trailingTimer == null) {
        trailingTimer = setTimeout(() => {
          trailingTimer = null;
          redraw();
        }, throttleMs - (now - lastSetDataAt));
      }
      return;
    }
    lastSetDataAt = now;

    const windowMin = props.replayTimeWindowMin && props.replayTimeWindowMin > 0
      ? props.replayTimeWindowMin
      : LIVE_MAP_DEFAULT_TIME_WINDOW_MIN;
    const startMs = playheadMs - windowMin * 60 * 1000;

    for (const fid of streamedFrameIds()) {
      ensureLayer(map, fid);
      const source = map.getSource(srcId(fid));
      if (!source) continue;
      const coords = liveColumnarStore.trackCoords(fid, startMs, playheadMs);
      // Never blank a track that HAS been drawn. `lineFeature` emits an empty FeatureCollection
      // below 2 points, so a window the ring cannot answer yet (mid-scrub, before hydration lands)
      // would wipe the line and flash. Holding the previous geometry for one cycle is strictly
      // better: it is stale by at most the throttle interval, and the next redraw corrects it.
      const prev = lastCoordCount.get(fid) ?? 0;
      if (coords.length < 2 && prev >= 2) continue;
      lastCoordCount.set(fid, coords.length);
      source.setData(lineFeature(coords));
    }
  };

  // Redraw: on playback clock + store epoch, throttled to the track cadence.
  createEffect(() => {
    void liveColumnarStore.epoch();
    void smoothPlaybackTimeForBoat();
    void isPlaying();
    void props.replayTimeWindowMin;
    redraw();
  });

  /**
   * Emphasise the highlighted boats and dim the rest.
   *
   * Paint-only, and deliberately separate from the redraw effect: emphasis does not change what
   * geometry a track has, so pushing it through `setData` would rebuild every GeoJSON source to
   * change a line width. With nothing highlighted every track returns to its normal weight, so the
   * "off" state is the untouched one.
   *
   * `line-opacity` multiplies the gradient rather than replacing it, so a dimmed track keeps the
   * fade from old to new that makes its direction of travel readable.
   */
  const applyEmphasis = () => {
    const map = props.map;
    if (!map || !styleReady()) return;
    const anyHighlighted = hasHighlightedSources();

    for (const fid of added) {
      const layerId = lyrId(fid);
      try {
        if (!map.getLayer(layerId)) continue;
        const name = liveColumnarStore.sourceName(fid);
        const on = !anyHighlighted || (name ? isSourceHighlighted(name) : false);
        map.setPaintProperty(layerId, 'line-width', on ? (anyHighlighted ? TRACK_WIDTH_HIGHLIGHTED : TRACK_WIDTH_NORMAL) : TRACK_WIDTH_NORMAL);
        map.setPaintProperty(layerId, 'line-opacity', on ? TRACK_OPACITY_NORMAL : TRACK_OPACITY_DIMMED);
      } catch (err) {
        debug('[GlTrackLayer] setPaintProperty failed', err);
      }
    }
  };

  createEffect(() => {
    void highlightedSources();
    void liveColumnarStore.epoch(); // names resolve from the registry; re-apply once they do
    applyEmphasis();
  });

  onCleanup(() => {
    if (trailingTimer != null) {
      clearTimeout(trailingTimer);
      trailingTimer = null;
    }
    const map = props.map;
    if (!map) return;
    for (const id of [...added]) removeLayer(map, id);
  });

  return null;
}
